/**
 * Route table — single edit point for adding new routes.
 */

import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { RouteErrorBoundary } from './RouteErrorBoundary';

// Core pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const Home2Page = lazy(() => import('@/pages/Home2Page'));
const SetupPage = lazy(() => import('@/pages/SetupPage'));
const DesignDemoPage = lazy(() => import('@/pages/DesignDemoPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const BlogListPage = lazy(() => import('@/pages/BlogListPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const PagePage = lazy(() => import('@/pages/PagePage'));
const PageSlugPage = lazy(() => import('@/pages/PageSlugPage'));

// Service pages (canonical)
const CybersecurityPage = lazy(() => import('@/pages/services/CybersecurityPage'));
const AwsCloudPage = lazy(() => import('@/pages/services/AwsCloudPage'));
const ManagedServicesPage = lazy(() => import('@/pages/services/ManagedServicesPage'));
const DevOpsPage = lazy(() => import('@/pages/services/DevOpsPage'));
const AiDataPage = lazy(() => import('@/pages/services/AiDataPage'));
const MonitoringPage = lazy(() => import('@/pages/services/MonitoringPage'));
const AiAdvisoryPage = lazy(() => import('@/pages/services/AiAdvisoryPage'));
const ServiceSlugPage = lazy(() => import('@/pages/services/ServiceSlugPage'));

// Service alias pages (menu-linked URLs)
const SecurityPage = lazy(() => import('@/pages/services/SecurityPage'));
const AiPage = lazy(() => import('@/pages/services/AiPage'));
const ManagedPage = lazy(() => import('@/pages/services/ManagedPage'));
const ManagedSecurityPage = lazy(() => import('@/pages/services/ManagedSecurityPage'));
const CloudPage = lazy(() => import('@/pages/services/CloudPage'));
const CostPage = lazy(() => import('@/pages/services/CostPage'));
const CompliancePage = lazy(() => import('@/pages/services/CompliancePage'));
const OperationsPage = lazy(() => import('@/pages/services/OperationsPage'));
const ModernizePage = lazy(() => import('@/pages/services/ModernizePage'));

// Products/solutions
const ProductsPage = lazy(() => import('@/pages/ProductsPage'));
const SolutionsPage = lazy(() => import('@/pages/SolutionsPage'));
const LogAnalysisPage = lazy(() => import('@/pages/products/LogAnalysisPage'));
const ComplianceScannerPage = lazy(() => import('@/pages/products/ComplianceScannerPage'));
const DarkWebMonitoringPage = lazy(() => import('@/pages/products/DarkWebMonitoringPage'));
const MizanGRCPage = lazy(() => import('@/pages/products/MizanGRCPage'));
const AndroidSecurityAuditorPage = lazy(() => import('@/pages/products/AndroidSecurityAuditorPage'));

// Solution sub-pages
const CloudLabsPage = lazy(() => import('@/pages/solutions/CloudLabsPage'));
const AiSolutionsPage = lazy(() => import('@/pages/solutions/AiSolutionsPage'));
const OperiStorePage = lazy(() => import('@/pages/solutions/OperiStorePage'));
const DarkWebPage = lazy(() => import('@/pages/solutions/DarkWebPage'));
const ThreatAnalyticsPage = lazy(() => import('@/pages/solutions/ThreatAnalyticsPage'));
const PhishingSimulationPage = lazy(() => import('@/pages/products/PhishingSimulationPage'));
const ProductSlugPage = lazy(() => import('@/pages/products/ProductSlugPage'));
const SolutionSlugPage = lazy(() => import('@/pages/solutions/SolutionSlugPage'));

// Industry detail page (handles all /industries/:slug routes)
const IndustryDetailPage = lazy(() => import('@/pages/industries/IndustryDetailPage'));

// Partner pages (reusable "Partners Module" — see PartnerPage.tsx)
const AwsPartnerPage = lazy(() => import('@/pages/partners/AwsPartnerPage'));
const ZscalerPartnerPage = lazy(() => import('@/pages/partners/ZscalerPartnerPage'));
const CrowdStrikePartnerPage = lazy(() => import('@/pages/partners/CrowdStrikePartnerPage'));
const MotadataPartnerPage = lazy(() => import('@/pages/partners/MotadataPartnerPage'));
const VeeamPartnerPage = lazy(() => import('@/pages/partners/VeeamPartnerPage'));
const MongoDbPartnerPage = lazy(() => import('@/pages/partners/MongoDbPartnerPage'));
const OsnPartnerPage = lazy(() => import('@/pages/partners/OsnPartnerPage'));
const PartnerSlugPage = lazy(() => import('@/pages/partners/PartnerSlugPage'));

// Case study detail page (handles all /case-studies/:slug routes)
const CaseStudySlugPage = lazy(() => import('@/pages/case-studies/CaseStudySlugPage'));

// New primary pages
const IndustriesPage = lazy(() => import('@/pages/IndustriesPage'));
const ResourcesPage = lazy(() => import('@/pages/ResourcesPage'));
const CaseStudiesPage = lazy(() => import('@/pages/CaseStudiesPage'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));
const CareersPage = lazy(() => import('@/pages/CareersPage'));
const JobDetailPage = lazy(() => import('@/pages/careers/JobDetailPage'));
const CareersLifePage = lazy(() => import('@/pages/CareersLifePage'));

// Resource sub-pages
const BestPracticesPage = lazy(() => import('@/pages/resources/BestPracticesPage'));
const FrameworksPage = lazy(() => import('@/pages/resources/FrameworksPage'));
const ToolsPage = lazy(() => import('@/pages/resources/ToolsPage'));
const PatternsPage = lazy(() => import('@/pages/resources/PatternsPage'));

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function wrap(element: ReactNode): ReactNode {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<div className="min-h-screen bg-slate-950" />}>{element}</Suspense>
    </RouteErrorBoundary>
  );
}

export const routes: RouteObject[] = [
  // Home
  { path: '/', element: wrap(<Home2Page />) },
  { path: '/home-old', element: wrap(<HomePage />) },
  // Utility
  { path: '/setupcms', element: wrap(<SetupPage />) },
  { path: '/design-demo', element: wrap(<DesignDemoPage />) },
  // Main pages
  { path: '/about', element: wrap(<AboutPage />) },
  { path: '/contact', element: wrap(<ContactPage />) },
  { path: '/pricing', element: wrap(<PricingPage />) },
  { path: '/industries', element: wrap(<IndustriesPage />) },
  { path: '/industries/:slug', element: wrap(<IndustryDetailPage />) },
  { path: '/case-studies', element: wrap(<CaseStudiesPage />) },
  { path: '/case-studies/:slug', element: wrap(<CaseStudySlugPage />) },
  // Pages redirected to Coming Soon (removed from active navigation)
  { path: '/partners', element: wrap(<ComingSoonPage />) },
  { path: '/partners/apply', element: wrap(<ComingSoonPage />) },
  { path: '/careers', element: wrap(<CareersPage />) },
  { path: '/careers/:jobId', element: wrap(<JobDetailPage />) },
  { path: '/life-at-otpl', element: wrap(<CareersLifePage />) },
  // Old nested URL redirect — keeps existing bookmarks/backlinks/SEO working.
  { path: '/careers/life-at-otpl', element: <Navigate to="/life-at-otpl" replace /> },
  { path: '/leadership', element: wrap(<ComingSoonPage />) },
  // Under-development pages — must not return 404
  { path: '/webinars', element: wrap(<ComingSoonPage />) },
  { path: '/news-events', element: wrap(<ComingSoonPage />) },
  // Partner pages — reusable CMS-driven "Partners Module" (see PartnerPage.tsx)
  { path: '/partners/aws', element: wrap(<AwsPartnerPage />) },
  { path: '/partners/crowdstrike', element: wrap(<CrowdStrikePartnerPage />) },
  { path: '/partners/zscaler', element: wrap(<ZscalerPartnerPage />) },
  { path: '/partners/motadata', element: wrap(<MotadataPartnerPage />) },
  { path: '/partners/veeam', element: wrap(<VeeamPartnerPage />) },
  { path: '/partners/mongodb', element: wrap(<MongoDbPartnerPage />) },
  // OSN (Operisoft Solutions Network) partner programme page — a static
  // page describing our OWN partner programme, not a `partner` CPT post,
  // so it must sit above the `/partners/:slug` catch-all below.
  { path: '/partners/osn-partner', element: wrap(<OsnPartnerPage />) },
  // Menu-linked aliases → the two anchored sections on the OSN page.
  // Both the "-partnership" and legacy "-partner" spellings redirect, so
  // existing CMS menu links / bookmarks keep working after the rename.
  { path: '/partners/referral-partnership', element: <Navigate to="/partners/osn-partner#referral-partnership" replace /> },
  { path: '/partners/strategic-partnership', element: <Navigate to="/partners/osn-partner#strategic-partnership" replace /> },
  { path: '/partners/referral-partner', element: <Navigate to="/partners/osn-partner#referral-partnership" replace /> },
  { path: '/partners/strategic-partner', element: <Navigate to="/partners/osn-partner#strategic-partnership" replace /> },
  { path: '/partners/channel', element: <Navigate to="/partners/osn-partner" replace /> },
  { path: '/partners/regional-resellers', element: wrap(<ComingSoonPage />) },
  { path: '/partners/global-distributors', element: wrap(<ComingSoonPage />) },
  { path: '/partners/system-integrators', element: wrap(<ComingSoonPage />) },
  // Reusable template for any future `partner` CPT post that doesn't have
  // a dedicated route above yet — see `PartnerSlugPage` docstring.
  { path: '/partners/:slug', element: wrap(<PartnerSlugPage />) },
  { path: '/technical-requirements', element: wrap(<ComingSoonPage />) },
  // Services — /services redirects to homepage services section
  { path: '/services', element: <Navigate to="/#services" replace /> },
  { path: '/services/cybersecurity', element: wrap(<CybersecurityPage />) },
  { path: '/services/aws-cloud', element: wrap(<AwsCloudPage />) },
  { path: '/services/managed-services', element: wrap(<ManagedServicesPage />) },
  { path: '/services/devops', element: wrap(<DevOpsPage />) },
  { path: '/services/ai-data', element: wrap(<AiDataPage />) },
  { path: '/services/monitoring', element: wrap(<MonitoringPage />) },
  { path: '/services/ai-ml-services', element: wrap(<AiAdvisoryPage />) },
  // Old slug redirect — keeps existing bookmarks/backlinks/SEO working.
  { path: '/services/ai-advisory', element: <Navigate to="/services/ai-ml-services" replace /> },
  // Services — menu-linked alias routes (fixes URL mismatches)
  { path: '/services/security', element: wrap(<SecurityPage />) },
  { path: '/services/ai', element: wrap(<AiPage />) },
  { path: '/services/managed', element: wrap(<ManagedPage />) },
  { path: '/services/managed-security', element: wrap(<ManagedSecurityPage />) },
  { path: '/services/cloud', element: wrap(<CloudPage />) },
  { path: '/services/cost', element: wrap(<CostPage />) },
  { path: '/services/compliance', element: wrap(<CompliancePage />) },
  { path: '/services/operations', element: wrap(<OperationsPage />) },
  { path: '/services/modernize', element: wrap(<ModernizePage />) },
  // Reusable template for any future `service` CPT post that doesn't have
  // a dedicated route above yet — see `ServiceSlugPage` docstring.
  { path: '/services/:slug', element: wrap(<ServiceSlugPage />) },
  // Products
  { path: '/products', element: wrap(<ProductsPage />) },
  { path: '/products/log-analysis', element: wrap(<LogAnalysisPage />) },
  { path: '/products/compliance-scanner', element: wrap(<ComplianceScannerPage />) },
  { path: '/products/dark-web-monitoring', element: wrap(<DarkWebMonitoringPage />) },
  { path: '/products/android-security-auditor', element: wrap(<AndroidSecurityAuditorPage />) },
  // Reusable template for any future `product` CPT post published under
  // /products that doesn't have a dedicated route above yet — see
  // `ProductSlugPage`'s docstring (mirrors `ServiceSlugPage`). Also what
  // makes a Quick Edit slug rename fully self-service for such posts.
  { path: '/products/:slug', element: wrap(<ProductSlugPage />) },
  // Solutions
  { path: '/solutions', element: wrap(<SolutionsPage />) },
  { path: '/solutions/mizan', element: wrap(<MizanGRCPage />) },
  { path: '/solutions/cloudlabs', element: wrap(<CloudLabsPage />) },
  { path: '/solutions/ai', element: wrap(<AiSolutionsPage />) },
  { path: '/solutions/store', element: wrap(<OperiStorePage />) },
  { path: '/solutions/darkweb', element: wrap(<DarkWebPage />) },
  { path: '/solutions/threat-analytics', element: wrap(<ThreatAnalyticsPage />) },
  // Reusable template for any future `product` CPT post published under
  // /solutions that doesn't have a dedicated route above yet — see
  // `SolutionSlugPage`'s docstring.
  { path: '/solutions/:slug', element: wrap(<SolutionSlugPage />) },
  { path: '/products/phishing-simulation', element: wrap(<PhishingSimulationPage />) },
  // Blog
  { path: '/blog', element: wrap(<BlogListPage />) },
  { path: '/blog/:slug', element: wrap(<BlogPostPage />) },
  // Search
  { path: '/search', element: wrap(<SearchPage />) },
  // Resources
  { path: '/resources', element: wrap(<ResourcesPage />) },
  { path: '/resources/best-practices', element: wrap(<BestPracticesPage />) },
  { path: '/resources/frameworks', element: wrap(<FrameworksPage />) },
  { path: '/resources/tools', element: wrap(<ToolsPage />) },
  { path: '/resources/patterns', element: wrap(<PatternsPage />) },
  // Legal & policy pages
  { path: '/privacy', element: wrap(<PagePage slug="privacy" />) },
  { path: '/terms', element: wrap(<PagePage slug="terms" />) },
  { path: '/cookies', element: wrap(<PagePage slug="cookies" />) },
  // Generic CMS pages template — lets an admin publish any new WordPress
  // `page` post and have it live at /pages/{slug} immediately, no frontend
  // deploy required. Dedicated routes above (privacy/terms/cookies, and
  // /about, /services, etc. elsewhere in this file) keep their own fixed
  // paths; this is the reusable fallback template for everything else.
  { path: '/pages/:slug', element: wrap(<PageSlugPage />) },
  // 404
  { path: '*', element: wrap(<NotFoundPage />) },
];
